'use strict';

// Public document identities only. Translations live in
// backend/content/public/locales/<locale>/; see docs/PUBLIC_LOCALIZATION.md.
// Registering a locale does not publish it: the server release allowlist owns
// that decision. Application/API routes are never language-prefixed.
const marketingPaths = Object.freeze([
  '/', '/features', '/pricing', '/download', '/features/images',
  '/features/documents', '/features/spreadsheets', '/features/presentations',
  '/features/files', '/features/videos', '/features/music', '/features/tasks',
  '/features/agents', '/features/models', '/features/tools', '/features/memory',
  '/features/prompts', '/features/explore', '/voice', '/api', '/mac', '/extension',
]);
const authPaths = Object.freeze([
  '/login', '/signup', '/forgot-password', '/reset-password', '/login-authorize',
]);
const pilotPaths = Object.freeze([...marketingPaths, ...authPaths]);
const locales = Object.freeze({
  en: Object.freeze({ code: 'en', label: 'English', htmlLang: 'en', direction: 'ltr', prefix: '', appleLocale: 'en_US' }),
  es: Object.freeze({ code: 'es', label: 'Español', htmlLang: 'es', direction: 'ltr', prefix: '/es', appleLocale: 'es_ES' }),
});
const paths = new Set(pilotPaths);

function normalizeLocale(value) {
  return typeof value === 'string' && Object.hasOwn(locales, value) ? value : null;
}

function cleanPath(value) {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') ||
      /[\\\u0000-\u0020\u007f?#]/.test(value) || /%(?:2f|5c|00)/i.test(value)) return null;
  return value.replace(/\/+$/, '') || '/';
}

function matchLocalizedPath(pathname) {
  const clean = cleanPath(pathname);
  if (!clean) return null;
  for (const locale of Object.values(locales)) {
    if (!locale.prefix) continue;
    if (clean === locale.prefix || clean.startsWith(locale.prefix + '/')) {
      const contentPath = clean.slice(locale.prefix.length) || '/';
      return { locale: locale.code, contentPath, available: paths.has(contentPath) };
    }
  }
  return null;
}

function hasTranslation(pathname, locale) {
  return !!normalizeLocale(locale) && (locale === 'en' || paths.has(cleanPath(pathname)));
}

function localizedPath(pathname, locale = 'en') {
  const clean = cleanPath(pathname);
  if (!clean || !hasTranslation(clean, locale)) return null;
  return locales[locale].prefix + (clean === '/' ? '/' : clean);
}

// Only same-origin, published document identities can become localized links.
// Query strings/fragments are retained as URL data, never interpreted as a
// destination. Store links, mailto links, APIs, assets and app paths pass through.
function localizeHref(href, locale = 'en', origin = 'https://gab.ai') {
  if (!normalizeLocale(locale) || typeof href !== 'string' || !href || href.startsWith('#')) return href;
  try {
    const url = new URL(href, origin);
    if (url.origin !== origin || !['http:', 'https:'].includes(url.protocol)) return href;
    const existing = matchLocalizedPath(url.pathname);
    const pathname = existing ? existing.contentPath : cleanPath(url.pathname);
    if (!pathname || !paths.has(pathname)) return href;
    const localized = localizedPath(pathname, locale);
    return localized + url.search + url.hash;
  } catch { return href; }
}

function alternatePaths(pathname, enabled = ['en']) {
  const available = [...new Set(['en', ...enabled])].filter(locale => normalizeLocale(locale) && hasTranslation(pathname, locale));
  return available.map(locale => ({ ...locales[locale], href: localizedPath(pathname, locale) }));
}

module.exports = {
  locales, marketingPaths, authPaths, pilotPaths, normalizeLocale, cleanPath,
  matchLocalizedPath, hasTranslation, localizedPath, localizeHref, alternatePaths,
};
